import type { TOC } from '@ember/component/template-only';
import { array, fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { filterBy } from '@nullvoxpopuli/ember-composable-helpers/helpers/filter-by';
import { isTesting } from '@embroider/macros';
import can from 'ember-can/helpers/can';
import formatTime from 'ember-intl/helpers/format-time';
import and from 'ember-truth-helpers/helpers/and';
import gt from 'ember-truth-helpers/helpers/gt';
import not from 'ember-truth-helpers/helpers/not';
import ArchiveJournalEventButton from 'parog-web/components/archive-journal-event-button';
import type {
  default as Column,
  default as JesHistoryTableColumnComponent
} from 'parog-web/components/jes-history-table/column';
import type JournalEventModel from 'parog-web/models/journal-event';
import fuiPopup from 'wise-fui-modules/modifiers/fui-popup';
import type JesHistoryTableComponent from '../jes-history-table';
import type { RowData } from '../jes-history-table';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    hasTimePopup?: boolean;
    column: Column;
    je: JournalEventModel;
  };
}

interface S2 {
  Args: { record: Record };
}

/**
 * Renders the span that will receive the value for the JE,
 * and sets a the popup if necessary
 * */
const Value = <template>
  {{#if @record.args.hasTimePopup}}
    <span {{@record.args.column.addValue @record.args.je}} {{fuiPopup}}>
      {{! content will be replaced by an in-element call }}
    </span>

    <div class='ui popup'>
      à
      {{formatTime @record.args.je.occurredAt format='hhmm'}}
    </div>
  {{else}}
    <span {{@record.args.column.addValue @record.args.je}}>
      {{! content will be replaced by an in-element call }}
    </span>
  {{/if}}
</template> satisfies TOC<S2>;

/**
 * If the JE has comments, renders the comment icon
 * that will display the comments when hovered
 * */
const Comments = <template>
  {{#if @record.args.je.comments}}
    <i
      class='info circle fitted icon'
      data-content={{@record.args.je.comments}}
      {{fuiPopup inline=true}}
    ></i>
  {{/if}}
</template> satisfies TOC<S2>;

/**
 * Renders the span that will receive the "edit" button to edit the JE,
 * if the column was configured for it
 * */
const EditJe = <template>
  {{#if @record.args.column.fullComponentId}}
    <span {{@record.args.column.addEdit @record.args.je}}>
      {{! content will be replaced by an in-element call }}
    </span>
  {{/if}}
</template> satisfies TOC<S2>;

/**
 * Renders the ArchiveJournalEventButton for the JE,
 * if the column was configured for it and
 * if we are allowed to
 * */
const ArchiveJe = <template>
  {{#unless @record.args.column.args.shouldHideArchiveButtons}}
    {{#if (can 'archive form' @record.args.column.fullComponentId)}}
      <ArchiveJournalEventButton
        @journalEvent={{@record.args.je}}
        @additionalInformation={{@record.args.column.args.customDeleteMessage}}
        class='ui tertiary fitted primary button [.show-shyone-on-hover:hover_&]:![opacity:1] [@media(hover:hover)]:![opacity:0] [@media(hover:hover)]:[transition:all_100ms_ease-in-out]'
      />
    {{/if}}
  {{/unless}}
</template> satisfies TOC<S2>;

/**
 * A JEsHistoryTable Record represents the value of a JE that will be displayed
 * in a JEsHistoryTable Cell
 *
 * A Cell can contain 0, 1, or multiple Records
 */
class Record extends Component<Signature> {
  /**
   * Should we render the edit and archive buttons for this record?
   *
   * true in test mode to improve DX and help devs "click" these buttons
   * without the need to previously "hover" them
   *
   * false otherwise to avoid rendering these UI elements for each Record
   * which leads to bad perfs
   */
  @tracked shouldRenderEditAndArchive = isTesting();

  <template>
    <div
      data-test-record='{{get @je.constructor "type"}}-{{@je.id}}'
      {{on 'mouseenter' (fn (mut this.shouldRenderEditAndArchive) true)}}
      ...attributes
    >
      <Value @record={{this}} />
      <Comments @record={{this}} />

      {{#if this.shouldRenderEditAndArchive}}
        <EditJe @record={{this}} />
        <ArchiveJe @record={{this}} />
      {{/if}}
    </div>
  </template>
}

/**
 * A JEsHistoryTable Cell can contain 0, 1, or multiple JEsHistoryTable Records
 *
 * Records inside a Cell are ordered by descending occurredAt
 */
const Cell = <template>
  {{#each (filterBy (array 'isArchived' false @jes)) as |je|}}
    <div>
      <Record
        @column={{@column}}
        @je={{je}}
        @hasTimePopup={{and
          (not @column.isTimePopupDisabled)
          (gt @jes.length 1)
        }}
      />
    </div>
  {{else}}
    -
  {{/each}}
</template> satisfies TOC<{
  Args: {
    column: JesHistoryTableColumnComponent;
    jes: JournalEventModel[];
  };
}>;

/**
 * A JEsHistoryTable Row renders a HTMLRowElement <tr>
 *
 * Its first <td> contains the result of the block yielded to "date"
 *
 * It will then loop on all the columns present in the JEsHistoryTable component
 * and render a <td> containing a JEsHistoryTable Cell
 */
const Row = <template>
  <tr ...attributes>
    <td>
      {{yield to='date'}}
    </td>

    {{#each @table.columns as |column index|}}
      <td class='show-shyone-on-hover'>
        <Cell @jes={{get @data index}} @column={{column}} />
      </td>
    {{/each}}
  </tr>
</template> satisfies TOC<{
  Element: HTMLTableRowElement;
  Args: {
    table: JesHistoryTableComponent;
    data: RowData;
  };
  Blocks: {
    date: [];
  };
}>;

export default Row;
